/* TeamActivityFeed — iter98.99eb
 *
 * Renders below the Shared Inbox table. Last 24h of:
 *   self_claim     → "{Actor} claimed {Lead Name}"
 *   auto_release   → "Auto-released {Lead Name} (was {Actor})"
 *   assignment     → "{Admin} assigned {Lead Name} to {Assignee}"
 *   reassignment   → "{Admin} reassigned {Lead Name} → {Assignee}"
 *
 * Click → opens the lead's detail page. Self-actions are highlighted in
 * primary tint so the PES can scan their own throughput at a glance.
 *
 * Backed by GET /api/coe/leads-inbox/recent-activity (24h window,
 * 30-row cap). Refetched whenever the parent triggers a refresh.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Users, Hand, ArrowsClockwise, UserSwitch, ClockClockwise, ArrowRight,
} from "@phosphor-icons/react";


function relTime(iso) {
    if (!iso) return "—";
    try {
        const ms = Date.now() - new Date(iso).getTime();
        if (ms < 60_000) return "just now";
        const mins = Math.floor(ms / 60_000);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    } catch { return "—"; }
}


const KIND_LABEL = {
    web:         "Web lead",
    b2b:         "Clinic",
    application: "Application",
};


/* Compose a short, scannable sentence per event. We keep it deliberately
 * one-line — the deep-link is the action, and the goal is "see fairness
 * at a glance", not "read a story". */
function describe(row) {
    const kindWord = KIND_LABEL[row.lead_kind] || "lead";
    const leadName = row.lead_name || "a lead";
    switch (row.event_type) {
        case "self_claim":
            return {
                Icon: Hand,
                tone: "primary",
                action: row.is_self ? "You claimed" : `${row.actor_name} claimed`,
                target: leadName,
                tail: kindWord,
            };
        case "auto_release":
            return {
                Icon: ClockClockwise,
                tone: "amber",
                action: "Auto-released",
                target: leadName,
                tail: row.from_assignee_name ? `was with ${row.from_assignee_name}` : "stale claim",
            };
        case "assignment":
            return {
                Icon: ArrowRight,
                tone: "violet",
                action: `${row.actor_name} assigned`,
                target: leadName,
                tail: row.to_assignee_name ? `→ ${row.to_assignee_name}` : "",
            };
        case "reassignment":
            return {
                Icon: UserSwitch,
                tone: "cyan",
                action: `${row.actor_name} reassigned`,
                target: leadName,
                tail: row.to_assignee_name ? `→ ${row.to_assignee_name}` : "",
            };
        default:
            return {
                Icon: Hand,
                tone: "neutral",
                action: row.actor_name || "Activity",
                target: leadName,
                tail: kindWord,
            };
    }
}


const TONE_CLS = {
    primary: "bg-primary/10 text-primary",
    amber:   "bg-amber-100 text-amber-800",
    violet:  "bg-violet-100 text-violet-700",
    cyan:    "bg-cyan-100 text-cyan-700",
    neutral: "bg-secondary text-foreground",
};


export default function TeamActivityFeed({ refreshKey = 0 }) {
    const [data, setData] = useState({ rows: [], window_hours: 24 });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/coe/leads-inbox/recent-activity");
            setData(r.data || { rows: [], window_hours: 24 });
        } catch {
            // Non-critical strip — silent failure, no toast.
            setData({ rows: [], window_hours: 24 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load, refreshKey]);

    const rows = data.rows || [];

    return (
        <section
            className="rounded-2xl border border-border bg-card overflow-hidden"
            data-testid="pes-inbox-team-activity"
        >
            <header className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/20">
                <div className="flex items-center gap-2">
                    <Users size={16} weight="duotone" className="text-primary" />
                    <h2 className="font-display text-sm font-semibold tracking-tight">Team activity</h2>
                    <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        last {data.window_hours || 24}h
                    </span>
                </div>
                <span className="text-[11px] text-muted-foreground tabular-nums" data-testid="pes-team-activity-count">
                    {loading ? "loading…" : `${rows.length} event${rows.length === 1 ? "" : "s"}`}
                </span>
            </header>

            {loading ? (
                <div className="p-6 text-center text-xs text-muted-foreground">Loading recent activity…</div>
            ) : rows.length === 0 ? (
                <div className="p-8 text-center">
                    <div className="text-sm font-medium text-muted-foreground">Quiet stretch.</div>
                    <p className="text-[12px] text-muted-foreground/80 mt-1">
                        Nothing&apos;s been claimed, assigned, or auto-released in the last 24 hours.
                    </p>
                </div>
            ) : (
                <ul className="divide-y divide-border/60" data-testid="pes-team-activity-list">
                    {rows.map((row) => {
                        const meta = describe(row);
                        const Icon = meta.Icon;
                        const toneCls = TONE_CLS[meta.tone] || TONE_CLS.neutral;
                        return (
                            <li key={row.id}>
                                <button
                                    type="button"
                                    onClick={() => navigate(row.detail_link)}
                                    className={`w-full text-left px-5 py-3 flex items-center gap-3 transition-colors hover:bg-secondary/30 ${row.is_self ? "bg-primary/5" : ""}`}
                                    data-testid={`pes-team-activity-row-${row.id}`}
                                >
                                    <span className={`shrink-0 w-7 h-7 rounded-full grid place-items-center ${toneCls}`}>
                                        <Icon size={13} weight="fill" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13px] leading-tight truncate">
                                            <span className={row.is_self ? "font-semibold text-primary" : "font-semibold text-foreground"}>{meta.action}</span>
                                            <span className="text-foreground"> {meta.target}</span>
                                            {meta.tail && (
                                                <span className="text-muted-foreground"> · {meta.tail}</span>
                                            )}
                                        </div>
                                        <div className="text-[10.5px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mt-1">
                                            {KIND_LABEL[row.lead_kind] || row.lead_kind} · {relTime(row.created_at)}
                                        </div>
                                    </div>
                                    <ArrowRight size={12} weight="bold" className="text-muted-foreground/60 shrink-0" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
